import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

import { api } from "../lib/chalkApi";
import { useTeam } from "../contexts/TeamContext";
import { ChalkActionButton, ChalkCard } from "../components/chalk/primitives";

/**
 * /billing/success — landing after Stripe Checkout completion.
 *
 * The webhook is the sole source of truth for subscription activation, so this
 * page polls /subscriptions/me up to 30s and only declares success once the
 * tier flips to active. If polling times out we tell the user payment was
 * received and to refresh — never lie about activation state.
 */
const POLL_INTERVAL_MS = 1500;
const MAX_WAIT_MS = 30000;

export default function BillingSuccess() {
  const navigate = useNavigate();
  const { refreshTier } = useTeam();
  const [state, setState] = useState("polling"); // polling | active | timeout
  const [tier, setTier] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => {
    const started = Date.now();
    async function poll() {
      while (!cancelled.current) {
        try {
          const d = await api.mySubscription();
          if (d.subscription && (d.subscription.status === "active" || d.subscription.status === "trialing")) {
            setTier(d.subscription.tier);
            setState("active");
            await refreshTier();
            return;
          }
        } catch { /* keep polling */ }
        if (Date.now() - started >= MAX_WAIT_MS) {
          setState("timeout");
          return;
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
    }
    poll();
    return () => { cancelled.current = true; };
  }, [refreshTier]);

  function backToApp() {
    const last = (() => {
      try { return sessionStorage.getItem("chalk_post_billing_return"); } catch { return null; }
    })();
    try { sessionStorage.removeItem("chalk_post_billing_return"); } catch { /* ignore */ }
    navigate(last && last.startsWith("/app") ? last : "/app", { replace: true });
  }

  return (
    <div className="chalk-min-h-screen" style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--chalk-cream, #FBF8F2)", padding: 24,
    }}>
      <ChalkCard data-testid="billing-success-card" style={{ maxWidth: 420, width: "100%" }}>
        {state === "polling" && (
          <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)" }}>
              Finishing up
            </div>
            <h1 style={{ fontFamily: "var(--font-serif, 'DM Serif Display', serif)", fontSize: 24, color: "var(--chalk-board)", margin: 0 }}>
              Finishing your Chalk upgrade…
            </h1>
            <div style={{ fontSize: 13, color: "var(--chalk-muted)" }}>
              Hang tight — Stripe is letting us know you're paid up.
            </div>
            <Spinner />
          </div>
        )}
        {state === "active" && (
          <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 12 }}>
            <CheckCircle2 size={36} style={{ margin: "0 auto", color: "var(--chalk-green)" }} aria-hidden="true" />
            <h1 style={{ fontFamily: "var(--font-serif, 'DM Serif Display', serif)", fontSize: 26, color: "var(--chalk-board)", margin: 0 }}>
              {tier === "chalkboard" ? "Welcome to The Chalkboard, Coach." : "Clipboard+ is active."}
            </h1>
            <div style={{ fontSize: 14, color: "var(--chalk-muted)" }}>
              {tier === "chalkboard"
                ? "Erase and Redraw, Draw It Up, volunteers — all yours."
                : "Pinned teams + push reminders are on."}
            </div>
            <ChalkActionButton testId="billing-success-back" variant="primary" label="Back to Chalk" onClick={backToApp} />
          </div>
        )}
        {state === "timeout" && (
          <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-amber)" }}>
              Almost there
            </div>
            <h1 style={{ fontFamily: "var(--font-serif, 'DM Serif Display', serif)", fontSize: 22, color: "var(--chalk-board)", margin: 0 }}>
              Payment received.
            </h1>
            <div style={{ fontSize: 14, color: "var(--chalk-muted)" }}>
              We're still updating your account. Give it a moment, then refresh — your tier will be active any second.
            </div>
            <ChalkActionButton testId="billing-success-retry" variant="ghost" label="Back to Chalk" onClick={backToApp} />
          </div>
        )}
      </ChalkCard>
    </div>
  );
}

function Spinner() {
  return (
    <div
      aria-hidden="true"
      style={{
        margin: "0 auto", width: 28, height: 28, borderRadius: 999,
        border: "2px solid rgba(28,43,30,0.15)", borderTopColor: "var(--chalk-board)",
        animation: "chalk-spin 0.8s linear infinite",
      }}
    >
      <style>{`@keyframes chalk-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
